import { notification, Spin } from 'antd'
import { t } from 'i18next'
import React, { createContext, useEffect, useRef, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useLocalStorage } from 'react-use'
import { get } from '../Common/api'
import { lbmNotification } from '../Common/notification'
import consumer from '../Common/cable'
import dayjs from 'dayjs'

const GlobalContext = createContext()
export default GlobalContext

export const GlobalContextProvider = ({ children }) => {
  const [user, setUser] = useLocalStorage('user', { isAuth: true })
  const [boxes, setBoxes] = useLocalStorage('boxes')
  const [slots, setSlots] = useLocalStorage('slots')
  const [owners, setOwners] = useLocalStorage('owners', [])
  const [events, setEvents] = useLocalStorage('events', [])
  const [inCharge, setInCharge] = useLocalStorage('inCharge', [])
  const [filters, setFilters] = useLocalStorage('filters', [])
  const [tags, setTags] = useLocalStorage('tags', [])
  const [openChats, setOpenChats] = useLocalStorage('chats', [])
  const [box, setBox] = useState()
  const [slot, setSlot] = useState()
  const [event, setEvent] = useState()
  const [openChat, setOpenChat] = useState()
  const [loading, setLoading] = useState(false)
  const notificationOnRef = useRef(false)
  const [notificationApi, notificationContextHolder] = notification.useNotification()

  // Slots state
  const [slotsDate, setSlotsDate] = useState(dayjs())

  const navigate = useNavigate()

  useEffect(() => {
    const boxSubscription = consumer.subscriptions.create({ channel: 'BoxesChannel' }, {
      connected: () => { console.log('box connected') },
      disconnected: () => { console.log('box disconnected') },
      received: (res) => { setBox(res.box) }
    })
    const slotSubscription = consumer.subscriptions.create({ channel: 'SlotsChannel' }, {
      connected: () => { console.log('slot connected') },
      disconnected: () => { console.log('slot disconnected') },
      received: (res) => { setSlot(res.slot) }
    })
    const eventSubscription = consumer.subscriptions.create({ channel: 'EventsChannel' }, {
      connected: () => { console.log('event connected') },
      disconnected: () => { console.log('event disconnected') },
      received: (res) => { setEvent({ ...res, content: res.content_trix_html }) }
    })
    const chatSubscription = consumer.subscriptions.create({ channel: 'OpenChatsChannel' }, {
      connected: () => { console.log('chat connected') },
      disconnected: () => { console.log('chat disconnected') },
      received: (res) => { setOpenChat(res.chat) }
    })
    let notifSubscription = false
    if (!notificationOnRef.current) {
      notificationOnRef.current = true
      notifSubscription = consumer.subscriptions.create({ channel: 'NotifyChannel' }, {
        connected: () => { console.log('notif connected') },
        disconnected: () => { console.log('notif disconnected') },
        received: (res) => { lbmNotification(navigate, res, notificationApi) }
      })
    }
    getBoxes()
    getInCharge()
    getOwners()
    getFilters()
    getTags()
    getOpenChats()
    return () => {
      boxSubscription.unsubscribe()
      slotSubscription.unsubscribe()
      eventSubscription.unsubscribe()
      chatSubscription.unsubscribe()
      if (notifSubscription) {
        notifSubscription.unsubscribe()
      }
    }
  }, [consumer])

  useEffect(() => {
    if (box) {
      updateBoxes([box])
    }
  }, [box])

  useEffect(() => {
    if (slot) {
      updateSlots([slot])
    }
  }, [slot])

  useEffect(() => {
    if (event) {
      updateEvents([event])
    }
  }, [event])

  useEffect(() => {
    if (openChat) {
      updateOpenChats([openChat])
    }
  }, [openChat])

  const errorMessages = (errors, traductionPath) => {
    return Object.keys(errors).map(key => (
      <>{t(traductionPath + '.' + key) + ': ' + errors[key]}</>
    ))
  }

  const getSlots = async () => {
    setLoading(true)
    await get({
      url: 'admin/slots',
      params: { scope: 'today' },
      onSuccess: (res) => {
        setSlots(res.map((slot) => ({ ...slot, assigned_short_name: !slot.assigned_short_name ? '' : slot.assigned_short_name })))
        setLoading(false)
      }
    })
  }

  const updateSlots = (newSlots) => {
    let savedSlots = slots || []
    newSlots = newSlots.map((slot) => ({ ...slot, assigned_short_name: !slot.assigned_short_name ? '' : slot.assigned_short_name }))
    newSlots.forEach((slot) => {
      const index = savedSlots.findIndex((b) => b.id === slot.id)
      if (index !== -1) {
        savedSlots[index] = { ...savedSlots[index], ...slot }
      } else {
        savedSlots = [...savedSlots, slot]
      }
    })
    setSlots([...savedSlots].sort((a, b) => dayjs(a.begin_date).diff(dayjs(b.begin_date))))
  }

  const getUnresolvedEvents = async () => {
    await get({
      url: 'admin/events',
      params: { resolved: false },
      notification: { api: notificationApi, state: 'get' },
      onSuccess: (res) => {
        updateEvents(res)
      }
    })
  }

  const getEvents = async (eventFilter) => {
    await get({
      url: 'admin/events',
      params: { ...eventFilter },
      onSuccess: (res) => {
        updateEvents(res)
      }
    })
  }

  const updateEvents = (newEvents) => {
    let savedEvents = events || []
    newEvents.forEach((event) => {
      const index = savedEvents.findIndex((b) => b.id === event.id)
      if (index !== -1) {
        savedEvents[index] = { ...savedEvents[index], ...event }
      } else {
        savedEvents = [...savedEvents, event]
      }
    })
    setEvents([...savedEvents])
  }

  const getBoxes = async () => {
    await get({
      url: 'admin/boxes',
      params: {},
      notification: { api: notificationApi, state: 'get' },
      onSuccess: (res) => {
        updateBoxes(res)
      }
    })
  }

  const updateBoxes = (newBoxes) => {
    let savedBoxes = boxes || []
    newBoxes.forEach((box) => {
      const index = savedBoxes.findIndex((b) => b.id === box.id)
      if (index !== -1) {
        savedBoxes[index] = { ...savedBoxes[index], ...box }
      } else {
        savedBoxes = [...savedBoxes, box]
      }
    })
    setBoxes([...savedBoxes])
  }

  const getInCharge = async () => {
    await get({
      url: 'admin/events/in_charge_options',
      notification: { api: notificationApi, state: 'get' },
      params: {},
      onSuccess: (res) => {
        setInCharge(res)
      }
    })
  }

  const getOwners = async () => {
    await get({
      url: 'admin/owners',
      notification: { api: notificationApi, state: 'get' },
      params: {},
      onSuccess: (res) => {
        setOwners(res)
      }
    })
  }

  const getFilters = async () => {
    await get({
      url: 'admin/event_filters/user',
      notification: { api: notificationApi, state: 'get' },
      params: { user_id: user.id },
      onSuccess: (res) => {
        setFilters(res)
      }
    })
  }

  const updateFilters = (filter) => {
    let savedFilters = filters || []
    const index = filters.findIndex((b) => b.id === filter.id)
    if (index !== -1) {
      savedFilters[index] = { ...savedFilters[index], ...filter }
    } else {
      savedFilters = [...savedFilters, filter]
    }
    setFilters([...savedFilters])
  }

  const getTags = async () => {
    await get({
      url: 'admin/tags/all',
      notification: { api: notificationApi, state: 'get' },
      params: {},
      onSuccess: (res) => {
        setTags(res)
      }
    })
  }

  const getOpenChats = async () => {
    await get({
      url: 'admin/chats',
      notification: { api: notificationApi, state: 'get' },
      params: { filter: 'open' },
      onSuccess: (res) => {
        setOpenChats(res)
      }
    })
  }

  const updateOpenChats = (newOpenChats) => {
    let savedOpenChats = openChats || []
    newOpenChats.forEach((chat) => {
      const index = savedOpenChats.findIndex((e) => e.id === chat.id)
      if (index !== -1) {
        savedOpenChats[index] = { ...savedOpenChats[index], ...chat }
      } else {
        savedOpenChats = [...savedOpenChats, chat]
      }
    })
    setOpenChats([...savedOpenChats])
  }

  const values = {
    // localStorage
    user,
    setUser,
    slots,
    boxes,
    owners,
    events,
    setEvents,
    inCharge,
    filters,
    tags,
    openChats,

    // getter
    getSlots,
    getBoxes,
    getOwners,
    getEvents,
    getUnresolvedEvents,
    getFilters,
    getTags,

    // updater
    updateSlots,
    updateBoxes,
    updateEvents,
    updateFilters,

    // methods
    errorMessages,
    notificationApi,
    slotsDate,
    setSlotsDate,
    openChat
  }

  return <GlobalContext.Provider value={values}>
    <Spin spinning={loading}>
      {notificationContextHolder}
      { children || <Outlet /> }
    </Spin>
  </GlobalContext.Provider>
}
